import {
  AiBuilderAbout,
  AiBuilderTurnInput,
  FlowRun,
  maskTypedSecrets,
} from '@activepieces/shared';
import { t } from 'i18next';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// From ai-builder-utils, NOT chat-message: that module pulls react-markdown
// (pure ESM) and would make this store untestable under Jest
import {
  AiBuilderChatMessage,
  PendingTurnInput,
  windowKeepingIntent,
} from './ai-builder-utils';

import { toast } from '@/components/ui/use-toast';
import { sentry } from '@/lib/sentry';

/**
 * What the next message should tell the server this conversation is about
 * ("Understand this error" from a failed run's details, or a step clicked in the canvas).
 * `about` is the ids-only shape the request carries. `run` is cached so a
 * `'run'` about's chip can reopen the run view (`setRun`) without a re-fetch;
 * a `'step'` about has none: its chip is plain text, not a link.
 */
export type PendingAiBuilderAbout = {
  about: AiBuilderAbout;
  label: string;
  run?: FlowRun;
};

interface ChatState {
  chats: Record<string, AiBuilderChatMessage[]>;
  /**
   * Flows with a turn running in THIS page. Never persisted: a turn lives on
   * this page's socket, so after a reload nothing runs here — which is exactly
   * what tells an answer that was lost from one still on its way.
   */
  turnsInFlight: Record<string, boolean>;
  /**
   * A turn's answer, waiting for the page-level owner (useApplyAiAnswers) to
   * apply it to the canvas. Set by the turn's onSuccess, cleared by the owner
   * through takeAnswerToApply — never persisted: the owner applies it once,
   * on the page that is open when the turn ends or when the flow is reopened,
   * never again after a reload.
   */
  answersToApply: Record<string, AiBuilderChatMessage | undefined>;
  /**
   * A connect-account gate (OPEN_AUTH) opened by an answer, waiting for its
   * connection to be created. Kept in the store, not the chat component, so
   * a remounted chat — or the page, sidebar closed — still sees it.
   */
  awaitingConnection: Record<string, boolean>;
  /**
   * One id per flow, telling the server which conversation a request
   * belongs to: stable across reloads (persisted, like `chats`),
   * renewed only when the user starts over (`clearMessages`).
   */
  conversationIds: Record<string, string>;
  /**
   * Set by the "Understand this error" button — never persisted: a reload must
   * not resurrect a stale pointer into a run the user has since moved on
   * from.
   */
  pendingAbout: Record<string, PendingAiBuilderAbout | undefined>;
  /**
   * The turn's own input, kept until its answer is stored (read by
   * `isAnswerLost`) — PERSISTED, unlike `turnsInFlight`: a page
   * reloaded mid-turn must still read the turn as lost, which a session-only
   * flag cannot do once the page that set it is gone.
   */
  pendingInput: Record<string, PendingTurnInput | undefined>;
  addMessage: (flowId: string, msg: AiBuilderChatMessage) => void;
  setMessages: (flowId: string, msgs: AiBuilderChatMessage[]) => void;
  clearMessages: (flowId: string) => void;
  /** Creates the flow's conversationId on first use, otherwise returns the existing one */
  getOrCreateConversationId: (flowId: string) => string;
  startTurn: (flowId: string) => void;
  endTurn: (flowId: string) => void;
  /** Set on every send, cleared once the turn settles — success or failure alike */
  setPendingInput: (
    flowId: string,
    input: AiBuilderTurnInput | undefined,
  ) => void;
  recordAnswerToApply: (flowId: string, answer: AiBuilderChatMessage) => void;
  /** Returns the pending answer and clears it in the same call */
  takeAnswerToApply: (flowId: string) => AiBuilderChatMessage | undefined;
  setAwaitingConnection: (flowId: string, awaiting: boolean) => void;
  setPendingAbout: (
    flowId: string,
    pending: PendingAiBuilderAbout | undefined,
  ) => void;
  /** Returns the pending "about" and clears it in the same call — attached to a sent message ONCE */
  takePendingAbout: (flowId: string) => PendingAiBuilderAbout | undefined;
}

/** Enough to reopen a conversation where it was left, not to archive it */
const MAX_PERSISTED_MESSAGES = 60;

export const CHAT_HISTORY_STORAGE_KEY = 'ai-builder-chat-history';

/** `Message`: what this page writes is today's shape; what it reads from disk may be older */
type StoredChatPayload<Message = StoredMessage> = {
  state?: {
    chats?: Record<string, Message[]>;
    conversationIds?: Record<string, string>;
    pendingInput?: Record<string, PendingTurnInput | undefined>;
  };
  version?: number;
};

/**
 * Flows THIS PAGE changed since its own last write: a write merges only these
 * into whatever is on disk, so a flow another tab wrote meanwhile is never
 * overwritten by one this page never touched. Accepted limit: the SAME flow
 * edited in two tabs inside one `storage`-event window — last write wins.
 */
const dirtyChatFlows = new Set<string>();
const markChatFlowDirty = (flowId: string): void => {
  dirtyChatFlows.add(flowId);
};

/**
 * localStorage is shared with the whole app and can be full for reasons that
 * have nothing to do with this store. A write that cannot happen must not take
 * the turn down with it: the conversation then lives for the session only.
 *
 * zustand's persist calls setItem on EVERY state change (turnsInFlight
 * included, though partialize excludes it): hence skipping an unchanged
 * payload, and reporting a quota failure once per page session.
 */
const quotaSafeStorage = createJSONStorage(() => {
  let hasReportedQuotaFailure = false;

  return {
    getItem: (name: string) => localStorage.getItem(name),
    setItem: (name: string, value: string) => {
      const incoming = JSON.parse(
        value,
      ) as StoredChatPayload<AiBuilderChatMessage>;
      const raw = localStorage.getItem(name);
      const onDisk = ((): StoredChatPayload | null => {
        if (!raw) return null;
        try {
          return JSON.parse(raw) as StoredChatPayload;
        } catch {
          return null;
        }
      })();
      // A tab still on the previous bundle rewrites the whole key in its own
      // shape: a flow carried over from disk is brought to today's shape, or
      // this write would stamp the current version over older messages
      const onDiskChats = toCurrentChats(onDisk?.state?.chats ?? {});
      // Flows on disk stay untouched; only the flows THIS page changed take
      // its value. Same for `conversationIds`: this page may lack a flow
      // another tab created, and writing them wholesale would erase its id.
      const mergedChats = { ...onDiskChats };
      const mergedConversationIds = {
        ...onDisk?.state?.conversationIds,
      };
      // Unlike chats/conversationIds, `pendingInput` genuinely clears to
      // absent: a dirty flow's value is taken AS IS, so a clear reaches disk
      const mergedPendingInput = { ...onDisk?.state?.pendingInput };
      for (const flowId of dirtyChatFlows) {
        const ownChats = incoming.state?.chats?.[flowId];
        if (ownChats !== undefined) mergedChats[flowId] = ownChats;
        const ownConversationId = incoming.state?.conversationIds?.[flowId];
        if (ownConversationId !== undefined) {
          mergedConversationIds[flowId] = ownConversationId;
        }
        mergedPendingInput[flowId] = incoming.state?.pendingInput?.[flowId];
      }
      dirtyChatFlows.clear();
      const merged: StoredChatPayload<AiBuilderChatMessage> = {
        ...incoming,
        state: {
          ...incoming.state,
          chats: mergedChats,
          conversationIds: mergedConversationIds,
          pendingInput: mergedPendingInput,
        },
      };
      const serialized = JSON.stringify(merged);
      // Against disk, not this tab's last write: another tab may have
      // rewritten the key since
      if (serialized === raw) return;
      try {
        localStorage.setItem(name, serialized);
      } catch (error) {
        if (hasReportedQuotaFailure) return;
        hasReportedQuotaFailure = true;
        sentry.captureException(error, 'AI builder chat history not saved');
        toast({
          title: t('Chat history not saved'),
          description: t(
            'This conversation stays available until you close the tab.',
          ),
        });
      }
    },
    removeItem: (name: string) => localStorage.removeItem(name),
  };
});

/**
 * Another tab's write rehydrates this one's view — `storage` only fires
 * in tabs OTHER than the one that wrote, which is exactly what is wanted:
 * this tab's own write already has the state it just produced.
 */
const rehydrateOnOtherTabWrite = (event: StorageEvent): void => {
  if (event.key !== CHAT_HISTORY_STORAGE_KEY) return;
  void useChatStore.persist.rehydrate();
};
if (typeof window !== 'undefined') {
  window.addEventListener('storage', rehydrateOnOtherTabWrite);
}
/** Test-only: each fresh module import (jest.resetModules) adds a listener on the same `window`, which would otherwise call a stale module's rehydrate */
export const stopListeningForOtherTabs = (): void => {
  window.removeEventListener('storage', rehydrateOnOtherTabWrite);
};

/**
 * `sourceInput`'s string-bearing variants, masked the way the server masks
 * the request (`maskTypedSecretsInTurn`). Reads `unknown`: this is data parsed
 * from disk, re-checked although it was already masked at send time.
 */
const maskedSourceInput = (
  sourceInput: unknown,
): AiBuilderChatMessage['sourceInput'] => {
  if (!sourceInput || typeof sourceInput !== 'object') return undefined;
  const input = sourceInput as AiBuilderChatMessage['sourceInput'];
  if (input?.type === 'text') {
    return { ...input, text: maskTypedSecrets(input.text) };
  }
  if (input?.type === 'card_reply') {
    return { ...input, label: maskTypedSecrets(input.label) };
  }
  return input;
};

const maskStoredSecrets = (
  message: AiBuilderChatMessage,
): AiBuilderChatMessage => ({
  ...message,
  // A key never carries a secret (it is a fixed chat-messages.ts constant);
  // only the free-text variant can hold what a user typed or the model echoed
  body:
    message.body.kind === 'text'
      ? { kind: 'text', text: maskTypedSecrets(message.body.text) }
      : message.body,
  prefix: message.prefix && maskTypedSecrets(message.prefix),
  sourceInput: maskedSourceInput(message.sourceInput),
});

/**
 * v2: typed `body`/`prefix` and `sourceInput`. The first release configured
 * no `version` and wrote zustand's default, a literal `"version":0`: zustand
 * calls `migrate` only when the stored `version` is a NUMBER different from
 * this one.
 */
const CHAT_HISTORY_VERSION = 2;

/**
 * The first release's shape, frozen independently of whatever
 * `AiBuilderChatMessage` becomes: reusing today's type for older history is
 * how a migration starts lying about what is on disk. Frozen here:
 * `content`/`contentParams`/`contentPrefix` ("params present ⇒ content is a
 * key"), `sourcePrompt` as a bracket-prefixed STRING (`[HELP_STEP] …`/
 * `[CARD_REPLY] {…}`) and `suggestedActions` as `{label, message}[]`. Every
 * other field has not changed since, so it is read from today's type.
 */
type StoredMessageV0 = Omit<
  AiBuilderChatMessage,
  'sourceInput' | 'suggestedActions' | 'body' | 'prefix'
> & {
  sourcePrompt?: string;
  content: string;
  contentParams?: Record<string, string>;
  contentPrefix?: string;
  suggestedActions?: { label: string; message: string }[];
};

/**
 * The intermediate shape: `sourcePrompt` is already `AiBuilderTurnInput`, but
 * the text is still the untyped `content`/`contentParams` convention and
 * `suggestedActions` still `{label, message}[]`.
 */
type StoredMessageV1 = Omit<StoredMessageV0, 'sourcePrompt'> & {
  sourcePrompt?: AiBuilderTurnInput;
};

/** A stored message, in the shape of whichever release wrote it */
type StoredMessage = StoredMessageV0 | StoredMessageV1 | AiBuilderChatMessage;

/**
 * `content`/`contentParams`/`contentPrefix` become the typed `body`/`prefix`.
 * `sourcePrompt`/`suggestedActions`/`card` are dropped, not converted: a turn
 * answered by the previous release is not replayable — its buttons answer a
 * turn the server no longer has. This also drops old "Need help?" payloads
 * that embedded the step's test output in clear.
 */
const typeLegacyMessage = (
  message: StoredMessageV0 | StoredMessageV1,
): AiBuilderChatMessage => {
  const {
    sourcePrompt: _sourcePrompt,
    content,
    contentParams,
    contentPrefix,
    suggestedActions: _suggestedActions,
    card: _card,
    ...rest
  } = message;
  return {
    ...rest,
    body:
      contentParams === undefined
        ? { kind: 'text', text: content }
        : { kind: 'key', key: content, params: contentParams },
    prefix: contentPrefix,
  };
};

/**
 * Read per message, not from the payload's `version`: a payload already
 * written with messages of two releases must still load, never crash
 * `maskStoredSecrets` (zustand swallows the error and the chat reads empty).
 */
const toCurrentChats = (
  chats: Record<string, StoredMessage[]>,
): Record<string, AiBuilderChatMessage[]> =>
  Object.fromEntries(
    Object.entries(chats).map(([flowId, messages]) => [
      flowId,
      messages.map((message) =>
        'body' in message ? message : typeLegacyMessage(message),
      ),
    ]),
  );

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      chats: {},
      turnsInFlight: {},
      answersToApply: {},
      awaitingConnection: {},
      conversationIds: {},
      pendingAbout: {},
      pendingInput: {},
      addMessage: (flowId, msg) => {
        markChatFlowDirty(flowId);
        set((state) => ({
          chats: {
            ...state.chats,
            [flowId]: state.chats[flowId]
              ? [...state.chats[flowId], msg]
              : [msg],
          },
        }));
      },
      setMessages: (flowId, msgs) => {
        markChatFlowDirty(flowId);
        set((state) => ({
          chats: {
            ...state.chats,
            [flowId]: msgs,
          },
        }));
      },
      clearMessages: (flowId) => {
        markChatFlowDirty(flowId);
        set((state) => ({
          chats: {
            ...state.chats,
            [flowId]: [],
          },
          // "New conversation": the server reads this as a different
          // conversationId and starts a fresh session thread
          conversationIds: {
            ...state.conversationIds,
            [flowId]: crypto.randomUUID(),
          },
          // Both tied to the conversation just left — a connect-account gate
          // it opened, or its unapplied answer — must not leak into the new
          // one. No paused-build pointer to reset: only the server keeps it,
          // keyed by conversationId.
          awaitingConnection: {
            ...state.awaitingConnection,
            [flowId]: false,
          },
          answersToApply: {
            ...state.answersToApply,
            [flowId]: undefined,
          },
        }));
      },
      getOrCreateConversationId: (flowId) => {
        const existing = get().conversationIds[flowId];
        if (existing) return existing;
        const id = crypto.randomUUID();
        // Same dirty set as `chats`: a conversationId is created exactly when
        // this page starts writing that flow's chat (see
        // quotaSafeStorage.setItem's per-flow merge)
        markChatFlowDirty(flowId);
        set((state) => ({
          conversationIds: { ...state.conversationIds, [flowId]: id },
        }));
        return id;
      },
      startTurn: (flowId) =>
        set((state) => ({
          turnsInFlight: { ...state.turnsInFlight, [flowId]: true },
        })),
      endTurn: (flowId) =>
        set((state) => ({
          turnsInFlight: { ...state.turnsInFlight, [flowId]: false },
        })),
      setPendingInput: (flowId, input) => {
        markChatFlowDirty(flowId);
        set((state) => ({
          pendingInput: {
            ...state.pendingInput,
            [flowId]:
              input === undefined
                ? undefined
                : { input, sentAt: new Date().toISOString() },
          },
        }));
      },
      recordAnswerToApply: (flowId, answer) =>
        set((state) => ({
          answersToApply: { ...state.answersToApply, [flowId]: answer },
        })),
      takeAnswerToApply: (flowId) => {
        const answer = get().answersToApply[flowId];
        if (!answer) return undefined;
        set((state) => ({
          answersToApply: { ...state.answersToApply, [flowId]: undefined },
        }));
        return answer;
      },
      setAwaitingConnection: (flowId, awaiting) =>
        set((state) => ({
          awaitingConnection: {
            ...state.awaitingConnection,
            [flowId]: awaiting,
          },
        })),
      setPendingAbout: (flowId, pending) =>
        set((state) => ({
          pendingAbout: { ...state.pendingAbout, [flowId]: pending },
        })),
      takePendingAbout: (flowId) => {
        const pending = get().pendingAbout[flowId];
        if (!pending) return undefined;
        set((state) => ({
          pendingAbout: { ...state.pendingAbout, [flowId]: undefined },
        }));
        return pending;
      },
    }),
    {
      name: CHAT_HISTORY_STORAGE_KEY,
      // A full history cannot be kept: the quota is shared with the rest of the
      // app, and a conversation grows without bound. What a reload needs is the
      // recent exchange, not the archive.
      storage: quotaSafeStorage,
      version: CHAT_HISTORY_VERSION,
      // The first release's payloads are at version 0, the intermediate
      // shape at version 1
      migrate: (stored, version) => {
        if (version >= CHAT_HISTORY_VERSION) return stored;
        const safeStored = (stored ?? {}) as {
          chats?: Record<string, StoredMessage[]>;
        };
        const migratedChats = toCurrentChats(safeStored.chats ?? {});
        // zustand's post-hydration write (`setItem()` right after
        // `migrate`+`merge`) keeps a flow's on-disk value unless THIS page
        // marked it dirty: without this, it would stamp the new version over
        // the unmigrated original, and the migration would never persist.
        for (const flowId of Object.keys(migratedChats))
          markChatFlowDirty(flowId);
        return { ...safeStored, chats: migratedChats };
      },
      // A conversation stored before secrets were masked at send time holds
      // them in clear, model echoes included: masked as it is read back
      merge: (persisted, current) => {
        const {
          chats = {},
          conversationIds = {},
          pendingInput = {},
        } = (persisted ?? {}) as NonNullable<StoredChatPayload['state']>;
        const diskChats = Object.fromEntries(
          Object.entries(toCurrentChats(chats)).map(([flowId, messages]) => [
            flowId,
            messages.map(maskStoredSecrets),
          ]),
        );
        return {
          ...current,
          // `rehydrateOnOtherTabWrite` calls this on EVERY write to the key.
          // Disk never carries the session-only fields `partialize` drops
          // (`previousFlowVersion`/`newFlowVersion`/`navigateDirectives`), so
          // a flow already held in memory keeps its own copy; only a flow not
          // loaded here is adopted from disk (all of them on first hydration).
          chats: { ...diskChats, ...current.chats },
          conversationIds: { ...conversationIds, ...current.conversationIds },
          // Disk wins, unlike chats: every set or clear of this tab's own
          // pendingInput is already written through, and keeping this tab's
          // copy would hold on to another tab's turn after it settled
          pendingInput,
        };
      },
      // Flow versions ride on messages for the SESSION's undo/retry only:
      // persisting them stores the whole flow twice per bot message and blows
      // the localStorage quota after a few conversations. Directives too: a
      // reopened chat replays them only for a turn that ended in this session.
      partialize: (state) => ({
        chats: Object.fromEntries(
          Object.entries(state.chats).map(([flowId, messages]) => [
            flowId,
            windowKeepingIntent(messages, MAX_PERSISTED_MESSAGES).map(
              ({
                previousFlowVersion: _previousFlowVersion,
                newFlowVersion: _newFlowVersion,
                navigateDirectives: _navigateDirectives,
                ...rest
              }) => rest,
            ),
          ]),
        ),
        // Stable across reloads, same as `chats`: a reload must never look
        // like a "new conversation" to the server
        conversationIds: state.conversationIds,
        // Survives a reload so an abandoned turn still reads as lost
        pendingInput: state.pendingInput,
      }),
    },
  ),
);
